// frontend/src/pages/AdminUsers.js
import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, updateUserRole, updateUserManager, getRoleTypes, getDepartments, getPrograms } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { Card, UserLink, Badge, Btn, Modal, Field, Input, Select, Spinner } from '../components/UI';
import PageHeader, { ViewOnlyNotice } from '../components/PageHeader';

const ROLE_COLOR  = { admin: 'red', dept_head: 'green', manager: 'amber', employee: 'blue' };
const ROLE_LABELS = { admin: 'Admin', dept_head: 'Department Head', manager: 'Manager', employee: 'Employee' };
const RT_COLOR   = { Developer:'blue', QA:'green', TechLead:'green', Architect:'blue', PM:'amber' };

export default function AdminUsers() {
  const { user } = useAuth();
  const { activeDepartmentId } = useActiveDepartment();
  const isAdmin    = user?.role === 'admin';
  const isDeptHead = user?.role === 'dept_head';
  // Department Heads can only WRITE while viewing their OWN department; other
  // departments (and "All") are read-only here. The backend enforces this too.
  const canManageHere = isAdmin || (isDeptHead && activeDepartmentId != null && Number(activeDepartmentId) === Number(user?.department_id));
  const [users,       setUsers]       = useState([]);
  // Everyone, regardless of the sidebar department — the "Reports to" picker
  // must be able to offer a manager in another department (a new department
  // often has no managers yet, and its head reports to someone outside it).
  const [allUsers,    setAllUsers]    = useState([]);
  const [departments, setDepartments] = useState([]);
  // Role types, grouped by program_id — so when the user picks a department
  // the dropdown below can show exactly that program's roles.
  const [rolesByProgram, setRolesByProgram] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);

  const emptyForm = { name: '', email: '', password: '', role: 'employee', role_type: '', department_id: '', department: '', job_title: '', manager_id: '' };
  const [form, setForm] = useState(emptyForm);

  // department_id: undefined overrides the sidebar scope the interceptor injects.
  const loadAllUsers = () => getUsers({ department_id: undefined })
    .then(d => setAllUsers(Array.isArray(d) ? d : [])).catch(() => setAllUsers([]));
  const load = () => {
    loadAllUsers();
    return getUsers().then(d => setUsers(Array.isArray(d) ? d : [])).catch(console.error);
  };
  useEffect(() => {
    Promise.all([
      getUsers().catch(() => []),
      getDepartments().catch(() => []),
      getPrograms().catch(() => []),
      loadAllUsers(),
    ]).then(async ([u, d, p]) => {
      setUsers(Array.isArray(u) ? u : []);
      setDepartments(Array.isArray(d) ? d : []);
      // Fetch role types per program in parallel so the form dropdown can
      // refresh instantly when the admin changes the department.
      const progs = Array.isArray(p) ? p : [];
      const entries = await Promise.all(
        progs.map(prog =>
          // department_id: undefined overrides the sidebar scope the axios
          // interceptor injects — with a department selected, the backend
          // would otherwise return THAT department's roles for every program.
          getRoleTypes({ program_id: prog.id, department_id: undefined })
            .then(list => [prog.id, Array.isArray(list) ? list : []])
            .catch(() => [prog.id, []])
        )
      );
      const map = {};
      entries.forEach(([k, v]) => { map[k] = v; });
      setRolesByProgram(map);
    }).finally(() => setLoading(false));
  }, []);

  // All role types flattened — used for table badge lookup only (independent
  // of which program the admin is currently scoped to).
  const allRoleTypes = Object.values(rolesByProgram).flat();

  // Departments → program_id lookup
  const deptProgramId = id => departments.find(d => String(d.id) === String(id))?.program_id || null;

  // Role types applicable to the department currently picked in the form:
  // its program's program-wide roles plus the department's own. Other
  // departments' roles are excluded — the backend rejects them on save.
  const rolesForDept = deptId => {
    const pid = deptProgramId(deptId);
    return pid
      ? (rolesByProgram[pid] || []).filter(r => r.department_id == null || String(r.department_id) === String(deptId))
      : [];
  };
  const formRoleTypes = rolesForDept(form.department_id);

  const openCreate = () => {
    setEditing(null);
    const defaultDept = isDeptHead ? String(user?.department_id || '') : (departments[0]?.id ? String(departments[0].id) : '');
    setForm({ ...emptyForm, department_id: defaultDept });
    setShowForm(true);
  };
  const openEdit   = u  => { setEditing(u); setForm({ ...u, password: '', manager_id: String(u.manager_id || ''), role_type: u.role_type || '', department_id: String(u.department_id || '') }); setShowForm(true); };

  const handleSubmit = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) {
        await updateUser(editing.id, form);
        // Role + reporting line go through their dedicated, department-guarded
        // endpoints (the generic PUT ignores them for department heads).
        if (form.role && form.role !== editing.role) await updateUserRole(editing.id, form.role);
        const newMgr = form.manager_id ? Number(form.manager_id) : null;
        if (newMgr !== (editing.manager_id || null)) await updateUserManager(editing.id, newMgr);
      } else {
        await createUser(form);
      }
      await load(); setShowForm(false);
    } catch (err) { alert(err.response?.data?.error || 'Error saving user'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Archive this user? They will be hidden from the system.')) return;
    await deleteUser(id); await load();
  };

  if (loading) return <Spinner />;

  // Department Heads manage people too — leaving them out meant a new dept
  // head's own team couldn't be put under them.
  // Dept heads may only pick a manager inside their own department (the
  // backend enforces it); admins may pick anyone.
  const managers = allUsers
    .filter(u => ['admin','dept_head','manager'].includes(u.role))
    .filter(u => !isDeptHead || Number(u.department_id) === Number(user?.department_id));

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="people" helpTask="add-people" subtitle={`${users.length} team members`} />
        {canManageHere && <Btn size="sm" onClick={openCreate}>+ Add user</Btn>}
      </div>

      {!canManageHere && <ViewOnlyNotice />}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)' }}>
              {['Name','Role','Role type','Department','Program','Job title','Manager','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                <td style={{ padding: '12px 16px' }}>
                  <UserLink id={u.id} name={u.name} initials={u.avatar_initials} subtitle={u.email} />
                </td>
                <td style={{ padding: '12px 16px' }}><Badge color={ROLE_COLOR[u.role]}>{ROLE_LABELS[u.role] || u.role}</Badge></td>
                <td style={{ padding: '12px 16px' }}>{u.role_type ? <Badge color={allRoleTypes.find(r=>r.key===u.role_type)?.badge_color || RT_COLOR[u.role_type] || 'gray'}>{allRoleTypes.find(r=>r.key===u.role_type)?.label || u.role_type}</Badge> : <span style={{ color:'var(--text-3)' }}>—</span>}</td>
                <td style={{ padding: '12px 16px' }}>{u.department_name ? <Badge color="gray">{u.department_name}</Badge> : (u.department ? <span style={{ color: 'var(--text-2)' }}>{u.department}</span> : <span style={{ color:'var(--text-3)' }}>—</span>)}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 12 }}>{u.program_name || '—'}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>{u.job_title || '—'}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-2)' }}>{u.manager_name || '—'}</td>
                <td style={{ padding: '12px 16px' }}>
                  {canManageHere ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn variant="secondary" size="sm" onClick={() => openEdit(u)}>Edit</Btn>
                      <Btn variant="ghost" size="sm" onClick={() => handleDelete(u.id)} style={{ color: 'var(--red-500)' }}>Archive</Btn>
                    </div>
                  ) : <span style={{ color: 'var(--text-3)', fontSize: 12 }}>View only</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? `Edit — ${editing.name}` : 'Add team member'}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Full name" required>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Aisha Kaur" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="aisha@energin.com.au" />
            </Field>
          </div>
          <Field label={editing ? 'New password (leave blank to keep)' : 'Password'} required={!editing}>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Role">
              <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                {isAdmin && <option value="admin">Admin</option>}
                {isAdmin && <option value="dept_head">Department Head</option>}
              </Select>
            </Field>
            <Field label="Reports to">
              <Select value={form.manager_id} onChange={e => setForm(f => ({ ...f, manager_id: e.target.value }))}>
                <option value="">No manager</option>
                {managers.filter(m => m.id !== editing?.id).map(m => <option key={m.id} value={m.id}>{m.name}{m.department_name ? ` — ${m.department_name}` : ''}</option>)}
              </Select>
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Department" required>
              <Select
                value={form.department_id}
                disabled={isDeptHead}
                onChange={e => {
                  const newDeptId = e.target.value;
                  const newProgramId = deptProgramId(newDeptId);
                  const allowed = newProgramId ? rolesForDept(newDeptId) : [];
                  const keepRoleType = allowed.some(r => r.key === form.role_type);
                  setForm(f => ({
                    ...f,
                    department_id: newDeptId,
                    // Auto-reset role_type if it doesn't exist in the new program
                    role_type: keepRoleType ? f.role_type : '',
                  }));
                }}>
                <option value="">Select department…</option>
                {(isDeptHead ? departments.filter(d => Number(d.id) === Number(user?.department_id)) : departments).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label="Job title">
              <Input value={form.job_title} onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} placeholder="Senior Engineer" />
            </Field>
          </div>
          {form.department_id && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: -6, marginBottom: 10 }}>
              Program: <strong>{departments.find(d => String(d.id) === String(form.department_id))?.program_name || '—'}</strong> · the user's KPI templates, role types, and review cycles come from this program.
            </div>
          )}
          {(() => {
            const currentValueStillValid = !form.role_type || formRoleTypes.some(r => r.key === form.role_type);
            return (
              <Field label="Role type (KPI set)">
                <Select
                  value={form.role_type}
                  onChange={e => setForm(f => ({ ...f, role_type: e.target.value }))}>
                  <option value="">
                    {form.department_id
                      ? (formRoleTypes.length ? 'None' : 'No role types defined for this department')
                      : 'Select a department first'}
                  </option>
                  {formRoleTypes.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                  {/* Legacy: current value belongs to a different program — keep it selectable so admin can see and fix it */}
                  {!currentValueStillValid && (
                    <option value={form.role_type}>{form.role_type} (legacy — not in this department)</option>
                  )}
                </Select>
                {form.department_id && formRoleTypes.length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--amber-700)', marginTop: 4 }}>
                    No role types are configured for this department yet. Add them in Admin → Role types (with this department selected in the sidebar).
                  </div>
                )}
                {!currentValueStillValid && (
                  <div style={{ fontSize: 11, color: 'var(--amber-700)', marginTop: 4 }}>
                    Current role type isn't defined for this department — pick a valid one or create it in Admin → Role types.
                  </div>
                )}
              </Field>
            );
          })()}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
            <Btn type="submit" disabled={saving || !form.department_id}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create user'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
